'use client';

/**
 * useApi — React hook returning the bound `apiFetch`.
 *
 *     const api = useApi()
 *     const me = await api<UserMe>('/auth/me')
 *
 * Subscribes the component to the auth store so the function identity
 * changes when the JWT changes — TanStack Query refetches automatically
 * after sign-in / sign-out.  The returned function is otherwise stable
 * across renders, safe to pass to TanStack Query's `queryFn`.
 */
import { useCallback } from 'react';

import { useAuthToken } from '@/lib/auth/useAuthToken';

import { apiFetch } from './client';
import type { ApiFetchOptions } from './types';

type ApiFn = <T = unknown>(path: string, options?: Omit<ApiFetchOptions, 'getToken'>) => Promise<T>;

export function useApi(): ApiFn {
  const token = useAuthToken();
  return useCallback(
    <T>(path: string, options?: Omit<ApiFetchOptions, 'getToken'>) =>
      apiFetch<T>(path, { ...(options ?? {}), getToken: () => token }),
    [token],
  );
}
